/* eslint-disable react/sort-comp */

import React, { Suspense, useMemo } from 'react';
import PageVisibility from 'react-page-visibility';
import { connect, useSelector } from 'react-redux';
import { withRouter } from 'react-router-dom';
import ReactToPrint from 'react-to-print';
import cn from 'classnames';
import moment from 'moment-timezone';
import { StripeCapitalAutoLaunch } from 'slickpay/capital/StripeCapitalAutoLaunch.component';

import { feClearOldAppointment } from 'actions/appointment.actions';
import createUpdateTimeoff from 'actions/booking/createUpdateTimeoff.action';
import feSetBookingStartTime from 'actions/booking/feSetBookingStartTime.action';
import fetchSalon from 'actions/fetchSalon.action';
import { deleteAllAppointmentsV1 } from 'actions/scheduler/deleteAllAppointments.action';
import fePickCurrentTime from 'actions/scheduler/fePickCurrentTime.action';
import fetchEventsBySalon from 'actions/scheduler/fetchEventsBySalon.action';
import fetchProceduresBySalon from 'actions/scheduler/fetchProceduresBySalon.action';
import getProcedureLog from 'actions/scheduler/getProcedureLog.action';
import undoProcedure from 'actions/scheduler/undoProcedure.action';
import updateProcedure from 'actions/scheduler/updateProcedure';
import bePutSalonFlagSettings from 'actions/sms/bePutSalonFlagSettings.action';
import fetchStylistsBySalon from 'actions/stylist/fetchStylistsBySalon.action';
import { feCleanCheckoutData, feSidebarClose, feSidebarOpen } from 'actions/ui.actions';
import navigateToCheckout from 'bl/checkout/navigateToCheckout.bl';
import setupCheckout from 'bl/checkout/setupCheckout.bl';
import { LT_SCHEDULER_DAY } from 'bl/loggers.bl';
import { fromPathnameToIsWeek } from 'bl/pathname.bl';
import determineHiddenStylistIds from 'bl/scheduler/determineHiddenStylistIds.bl';
import { ModalContext } from 'components/core/ModalRoot.component';
import ToggleFeature from 'components/core/ToggleFeature.component';
import SalonModal from 'components/dialog/SalonModal.component';
import SM from 'components/dialog/SalonModal.module.scss';
import Button from 'components/form/Button.component';
import Icon from 'components/generic/Icon.component';
import IconStatus from 'components/generic/IconStatus.component';
import AlertAllStylistsOff from 'components/scheduler/AlertAllStylistsOff.component';
import RebookAppointmentModal from 'components/sidebar/appointment/dialogs/RebookAppointmentModal.component';
import DepositLoadingPanel from 'components/sidebar/appointment/wizard/panel/DepositLoadingPanel.component';
import PUSHER from 'config/pusher.singleton';
import { USE_LOGGER_SCHEDAY, USE_OLD_CARD_PAYMENT_ERROR } from 'config/use.conf';
import DELAY from 'constants/delay.const';
import ELID from 'constants/elid.const';
import { STYLE_DISPLAY_NONE } from 'constants/misc.const';
import { AP_STATUS } from 'constants/payment/appointmentStatus.const';
import DEPOSIT_ACTION from 'constants/payment/depositAction.const';
import DEPOSIT_SOURCE from 'constants/payment/depositSource.const';
import STRIPE_STATUS from 'constants/pos/stripeStatus.const';
import SP from 'constants/ui/sidePanel.const';
import { useAppointmentPermissions } from 'hooks/useAppointmentPermissions.hook';
import useIsPersonalCalendarOnly from 'hooks/useIsPersonalCalendarOnly.hook';
import { useTimeOffPermissions } from 'hooks/useTimeOffPermissions.hook';
import { useTranslation, withTranslation } from 'providers/GILT.provider';
import { selectBooking, selectBookingNewAppointment, selectBookingNewRebookCreated, selectBookingOldAppointment } from 'selectors/booking.selectors';
import { selectCurrentSalonFlags, selectCurrentSalonHideDeletedStylistColumn, selectCurrentSalonOpeningHours, selectCurrentSalonStripeStatus } from 'selectors/currentSalon.selectors';
import selectEffectiveUserRole from 'selectors/selectEffectiveUserRole';
import { selectSidebarOpen, selectSidebarPanel } from 'selectors/sidebar.selectors';
import asArray from 'utils/arrays/asArray.util';
import fromAnyToBool from 'utils/converters/fromAnyToBool.util';
import fromAnyToNumber from 'utils/converters/fromAnyToNumber.util';
import { fromAnyDateToZulu } from 'utils/converters/time/from.util';
import mapActionsToDispatchToProps from 'utils/core/mapActionsToDispatchToProps.util';
import { formatSchedulerWeekUrlSegment, getSchedulerWeekEndSunday, getSchedulerWeekStartMonday, parseSchedulerWeekUrlSegment } from 'utils/date/schedulerWeekBound.util';
import delayed from 'utils/functions/delayed.util';
import alwaysNoop from 'utils/functions/nullary/alwaysNoop.util';
import logger from 'utils/logger.util';
import isNotNilValue from 'utils/predicates/any/isNotNilValue.util';
import isFullArray from 'utils/predicates/array/isFullArray.util';
import isFalsy from 'utils/predicates/isFalsy.util';
import isTruthy from 'utils/predicates/isTruthy.util';

import { changeAppointmentsStatus, clearDeleteAppointmentInfo, deleteAppointment, removeNewAppointment, setEndTime, updateService } from 'booking/actions';
import AlertDialog from 'checkout/components/AlertDialog';
import { purchaseSmsBundle } from 'clientCommunications/actions';
import { SMS_CREDITS_PUSHER } from 'clientCommunications/constants';
import { closeNotification, displayNotification } from 'notification/actions';
import { getCategories } from 'salon/actions';
import {
  clearNoshowChargeInfo,
  clearRequestDepositSuccess,
  createIfAllowed,
  deletePreference,
  emptyAppointmentStatuses,
  fetchAppointmentDetails,
  fetchClientLabels,
  fetchDeposit,
  hideInactiveStylists,
  markAppointmentEdit,
  pickCurrentStylist,
  reorderStylists,
  reorderStylistsThenSave,
  requestDeposit,
  revertProcedureLog,
  savePreference,
  saveStylistOrder,
  setBookedByButton,
  setCreation,
  setCurrentDay,
  setDragFrom,
  setDropTime,
  setModal,
  setView,
  showAllStylist,
  updateNewBookingDate,
} from 'scheduler/actions';
import { NoSmsCredit } from 'scheduler/components/common/NoSmsCredit';
import ProfileImageUploader from 'scheduler/components/common/ProfileImageUploader.component';
import { RebookAppointment } from 'scheduler/components/common/RebookAppointment';
import { REBOOK_TIME } from 'scheduler/components/common/RebookAppointmentDialog.component';
import SmsButtonNew from 'scheduler/components/common/SmsButtonNew';
import ComponentToPrint from 'scheduler/components/ComponentToPrint';
import DayScheduler from 'scheduler/components/DayScheduler';
import SchedulerHeaderWrap from 'scheduler/components/Header/SchedulerHeaderWrap.component';
import SmsPreferencesPopup from 'scheduler/components/SmsPreferencesPopup';
import SmsStoppedTextComponent from 'scheduler/components/Text/SmsStoppedText.component';
import WeekScheduler from 'scheduler/components/WeekScheduler';
import { DAY_VIEW, SCHEDULER_UPDATED_APPOINTMENT, SCHEDULER_UPDATED_EVENT, SCHEDULER_UPDATED_PROCEDURE } from 'scheduler/constants';
import { trackEvent } from 'tracking/actions';

import 'styles/calendar/index.scss';
import CN from './Scheduler.module.scss';

const L = logger('Scheduler'); // eslint-disable-line no-unused-vars
const L_DAY = logger(LT_SCHEDULER_DAY);

const USE_AUTO_OPEN_NEW_BOOKING = false;

// NOTE: this is used for document.queryElement() in Scheduler.rebook()
// and attached to renderedTodayButton of SchedulerHeader component
export const WORKAROUND_ID = 'workaround-id-for-old-btn-today';

const Sidebar = React.lazy(() => import('components/sidebar/Sidebar.component').then((module) => ({ default: module.Sidebar })));

// NOTE: Cypress doesn't use a browser with support for visual viewport,
// so it makes tests fail, because of that, do if check before using it

const VIEWPORT = window.visualViewport;

const LOADING_PROCEDURE_ID = 55;
const LOADING_EVENT_ID = 56;

class Scheduler extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      modalIsOpen: false,
      noShowAmount: 0,
      depositAction: '',
      deposit_amount: 0,
      deposit_source: '',
      authenticationRequired: false,
      showNewAppointmentModal: false,
      showGoToCheckoutModal: false,
      totalPrice: 0,
      clientName: '',
      todayProcedures: null,
      hardReloadDone: false,
      voucherDone: false,
      showRebookComponent: false,
      oldAppointment: null,
      isNewAppointment: false,
      seenGoToCheckout: false,
      showSms: false,
      procedure: null,
      modalRequestSend: false,
      modalNoSmsCredit: false,
      newAppointment: [],
      showRequestDepositPopup: false,
      originalWidth: window.innerWidth,
      smsStoppedModal: false,
      areAllStylistsOff: false,
      modalUploader: false,
      selectedStylistId: null,
    };

    this.componentRef = React.createRef();
  }

  UNSAFE_componentWillMount() {
    if (this.props.categoriesAndServices.length === 0) {
      this.props.getCategories(this.props.salonId);
    }
  }

  // noinspection JSCheckFunctionSignatures
  UNSAFE_componentWillReceiveProps(nextProps) {
    const FTAG = 'UNSAFE_componentWillReceiveProps()';

    const { currentDay: prevCurrentDay, newRebookCreated: prevNewRebookCreated } = this.props;
    const { currentDay: nextCurrentDay, newRebookCreated: nextNewRebookCreated } = nextProps;

    const isNewAppointment = this.props.location.pathname.indexOf('/appointment/new') > 0 && Object.keys(this.props.appointmentDetails).length > 0;

    this.setState({ isNewAppointment });

    if (false !== nextNewRebookCreated && prevNewRebookCreated !== nextNewRebookCreated) {
      L.debug(FTAG, 'newRebookCreated:', nextNewRebookCreated, '<-', prevNewRebookCreated);
    }

    if (REBOOK_TIME.before === nextNewRebookCreated?.from) {
      this.setState({ showGoToCheckoutModal: true, seenGoToCheckout: false });
    } else if (REBOOK_TIME.after === nextNewRebookCreated?.from) {
      this.props.cleanCheckoutData();
      this.props.emptyAppointmentStatuses();
    }

    if (nextProps?.location?.state) {
      if (nextProps?.location?.state?.sellVoucher && !this.state.voucherDone) {
        const retailSaleButton = document.querySelector('.retail-sale-button .action-button');
        if (retailSaleButton) retailSaleButton.click();
        const voucherSaleButton = document.querySelector('.sale-dropdown--container .dropdown-btn:nth-child(2)');
        if (voucherSaleButton) {
          voucherSaleButton.click();
          this.setState({ voucherDone: true });
        }
      }
    }

    if (nextProps.waiting_procedures) {
      nextProps.displayNotification('Loading...', 'info', false, LOADING_PROCEDURE_ID, 2000);
    } else {
      nextProps.closeNotification(LOADING_PROCEDURE_ID);
    }

    if (nextProps.procedures && nextProps.procedures.length > 0) {
      let { dateString } = this.props.match.params;
      if (dateString === 'today') {
        dateString = moment().format('YYYYMMDD');
      }
      const todayProcedures = nextProps.procedures.filter((p) => p.start_time && p.start_time.split('T')[0].split('-').join('') === dateString);

      this.setState({ todayProcedures });
    }

    if (nextProps.waiting_events) {
      nextProps.displayNotification('Loading...', 'info', false, LOADING_EVENT_ID, 2000);
    } else {
      nextProps.closeNotification(LOADING_EVENT_ID);
    }

    if (nextProps.update_events) {
      this.reloadEvents();
    }

    if (!nextCurrentDay.isSame(prevCurrentDay)) {
      if (!this.props.dayView) {
        this.setWeekView(nextCurrentDay, this.props.stylistId);
      } else {
        // when editing booking date
        const bookingWindow = document.querySelector('.BookingWindow');
        if (bookingWindow) {
          this.setDayView(nextCurrentDay, true);
        }
      }
    }

    if (nextProps.booking.appointmentStatuses.response && !this.state.modalIsOpen) {
      const depositAction = nextProps.booking.appointmentStatuses.response[0].action;
      const noShowAmount = nextProps.booking.appointmentStatuses.response[0].amount;
      if (!isNewAppointment && noShowAmount !== 0) {
        this.setState({ modalIsOpen: true, depositAction, noShowAmount });
      } else if (!isNewAppointment && this.state.deposit_amount && this.state.deposit_source === DEPOSIT_SOURCE.online) {
        // keep deposit in client account
        this.setState({
          modalIsOpen: true,
          depositAction: DEPOSIT_ACTION.keep,
          noShowAmount: this.state.deposit_amount,
        });
      }
    }
    if ((nextProps.noshowChargeSuccess || nextProps.booking.deposit_amount) && nextProps.booking.deletedAppointmentId && !isNewAppointment) {
      let noShowAmount;
      const depositAction = nextProps.noshow_action || nextProps.booking.depositAction;
      const { deposit_amount } = nextProps.booking;
      if (nextProps.noshowChargedAmount) {
        noShowAmount = nextProps.noshowChargedAmount;
      } else {
        noShowAmount = nextProps.booking.deposit_amount;
      }
      this.setState({
        modalIsOpen: true,
        depositAction,
        noShowAmount,
        deposit_amount,
        deposit_source: nextProps.booking.deposit_source,
      });
    }
    if (nextProps.appointmentCancelled) {
      let noShowAmount;
      const depositAction = nextProps.noshow_action || nextProps.booking.depositAction || '';
      let { deposit_amount } = nextProps.booking;
      if (nextProps.noshowChargedAmount) {
        noShowAmount = nextProps.noshowChargedAmount;
      } else {
        noShowAmount = nextProps.booking.deposit_amount;
      }
      if (!noShowAmount) {
        noShowAmount = 0;
      }
      if (!deposit_amount) {
        deposit_amount = 0;
      }
      const deposit_source = nextProps.booking.deposit_source || '';
      this.setState({
        modalIsOpen: true,
        depositAction,
        noShowAmount,
        deposit_amount,
        deposit_source,
      });
    }

    const isChargeErrorShown = USE_OLD_CARD_PAYMENT_ERROR && nextProps.noshowChargeError;
    if (!this.state.isNewAppointment && (isChargeErrorShown || this.props.errorMessage)) {
      this.setState({ modalIsOpen: true });
    }

    const newAppCreated = nextProps.newAppointment.length > 0;
    const isRebookAfterCheckout = REBOOK_TIME.before !== nextNewRebookCreated?.from;

    let newAppointmentInToday = true;
    if (nextProps.newAppointment && nextProps.newAppointment.length > 0) {
      const { newAppointment } = nextProps;

      const newAppArr = nextProps.procedures.filter((p) => p.appointment.id === newAppointment[0].id);
      if (newAppArr.length === 0) {
        newAppointmentInToday = false;
      }
      this.setState({ newAppointment });
    }

    if ((newAppCreated || isRebookAfterCheckout) && !this.state.showGoToCheckoutModal && nextProps.newAppointment.length && newAppointmentInToday) {
      this.setState({ showNewAppointmentModal: true }, () => {
        setTimeout(() => {
          this.notToRequestDeposit();
          this.setState({ showRequestDepositPopup: false });
        }, 7000); // request deposit modal disappear after 7s
      });

      const { newAppointment } = nextProps;

      if (newAppointment.length > 0) {
        const { procedures } = newAppointment[0];
        let totalPrice = 0;
        if (procedures && procedures.length > 0) {
          for (let i = 0; i < procedures.length; i += 1) {
            totalPrice += procedures[i].price;
          }
        }

        const clientName = newAppointment[0].client.user_name;
        const fixedTotal = parseFloat(totalPrice.toFixed(2));
        this.setState({ totalPrice: fixedTotal, clientName });
      }
    }

    if (nextProps.requestDepositSuccess && !this.state.modalRequestSend) {
      this.setState({ modalRequestSend: true });
    }

    const { events, timezone } = this.props;
    const isPersonalCalendar = fromAnyToBool(this.props.calendarPermissionPersonal);

    const hiddenStylistIds = isPersonalCalendar
      ? []
      : determineHiddenStylistIds({
          currentDay: prevCurrentDay,
          events,
          tz: timezone,
          tag: 'Scheduler',
        });

    const displayStylists = asArray(this.props.displayStylists);
    const newValue = hiddenStylistIds.length > 0 && displayStylists.length > 0 && displayStylists.length === hiddenStylistIds.length;

    delayed(DELAY.flash, () => void this.setState({ areAllStylistsOff: newValue }));
  }

  componentDidMount() {
    this.updateWindowDimensions();
    if (VIEWPORT) {
      VIEWPORT.addEventListener('resize', this.updateWindowDimensions);
    }
    this.subscribe();
    const currentView = this.currentView();

    if (currentView !== undefined) {
      const isDayView = currentView.toLowerCase() === DAY_VIEW.toLowerCase();

      const currentDateString = this.props.match.params.dateString;
      const salonTz = this.props.timezone;

      if (!isDayView) {
        if (currentDateString.toLowerCase() === 'active') {
          const today = moment();
          if (this.props.match.params.activeDateString) {
            const parseDate = moment(this.props.match.params.activeDateString, 'YYYYMMDD');
            if (parseDate.isValid()) {
              this.setDayView(parseDate, false);
            }
          } else {
            this.setDayView(today);
          }
        } else if (currentDateString.toLowerCase() === 'today') {
          if (currentDateString !== undefined) {
            const parseWeek = getSchedulerWeekStartMonday(salonTz ? moment.tz(salonTz) : moment());
            if (parseWeek.isValid()) {
              const currentStylistId = this.props.displayStylists[0].id;
              if (currentStylistId !== undefined && Number(currentStylistId)) {
                this.setWeekView(parseWeek, Number(currentStylistId));
              }
            }
          }
        } else if (currentDateString !== undefined) {
          const { addedServices } = this.props;
          let selectedDay;
          if (!this.props.isPosDeposit && addedServices && addedServices.length) {
            selectedDay = moment(addedServices[0].start_time);
          } else {
            selectedDay = parseSchedulerWeekUrlSegment(currentDateString, salonTz) || moment.invalid();
          }

          if (selectedDay.isValid()) {
            const currentStylistId = this.props.match.params.stylistId;
            if (currentStylistId !== undefined && Number(currentStylistId)) {
              this.setWeekView(selectedDay, Number(currentStylistId));
            }
          }
        }
      } else if (this.props.dayView !== isDayView) {
        if (isDayView && currentDateString) {
          if (currentDateString.toLowerCase() === 'today' || currentDateString.toLowerCase() === 'active') {
            this.setDayView(moment(this.props.match.params.activeDateString));
          } else if (currentDateString !== undefined) {
            const parseDate = moment(currentDateString, 'YYYYMMDD');
            if (parseDate.isValid()) {
              this.setDayView(parseDate, false);
            }
          }
        } else if (currentDateString !== undefined) {
          const parseWeek = parseSchedulerWeekUrlSegment(currentDateString, salonTz);
          if (parseWeek && parseWeek.isValid()) {
            const currentStylistId = this.props.match.params.stylistId;
            if (currentStylistId !== undefined && Number(currentStylistId)) {
              this.setWeekView(parseWeek, Number(currentStylistId));
            }
          }
        }
      } else if (this.props.dayView) {
        if (currentDateString && currentDateString.toLowerCase() === 'today') {
          this.setDayView(moment(), false);
        } else {
          const parseDate = moment(currentDateString, 'YYYYMMDD');
          if (parseDate.isValid()) {
            this.setDayView(parseDate, false);
          }
        }
      }
    }
  }

  componentWillUnmount() {
    if (VIEWPORT) {
      VIEWPORT.removeEventListener('resize', this.updateWindowDimensions);
    }
    this.props.closeNotification(LOADING_PROCEDURE_ID);
    this.props.closeNotification(LOADING_EVENT_ID);
    this.unsubscribe();
  }

  updateWindowDimensions = () => {
    if (!VIEWPORT) {
      L.warn('updateWindowDimensions()', 'window.visualViewport is not valid:', VIEWPORT);
      return;
    }

    const selector = '.card-modal-wraper.animated.zoomIn';
    const el = document.querySelector(selector);
    if (!el) {
      L.info('updateWindowDimensions()', 'no element found for selector:', selector);
      return;
    }

    const classList = el.classList;
    if (!classList) return;

    if (this.state.originalWidth === window.innerWidth) {
      classList.remove('scale-class');
    } else {
      classList.add('scale-class'); // zoom: 84%;
    }
  };

  onRequestClose = () => {
    this.setState({ modalIsOpen: false, depositAction: '', deposit_amount: 0, deposit_source: '' });
    this.props.emptyAppointmentStatuses();
    this.props.clearNoshowChargeInfo();
    if (this.props.errorMessage === 'authentication_required') {
      this.setState({ authenticationRequired: true });
    }
    this.props.clearDeleteAppointmentInfo();
  };

  stayOnCurrentDay = () => {
    const appointmentId = this.state.newAppointment?.[0]?.id;

    this.props.clearOldAppointment();
    this.props.cleanCheckoutData();
    this.setState({ showGoToCheckoutModal: false, seenGoToCheckout: true, newAppointment: [] });

    if (USE_AUTO_OPEN_NEW_BOOKING) {
      L.debug('stayOnCurrentDay()', 'appointmentId:', appointmentId, this.state, this.props);
      this.props.feSidebarOpen({ panel: SP.appointmentDefault, appointmentId, tag: 'Scheduler.stayOnCurrentDay()' });
    }
  };

  setDayView = (selectedDate, setHistory) => {
    const openBookingProcedure = this.props.booking;
    if (openBookingProcedure.startTime) {
      const setTime = openBookingProcedure.startTime;
      let currentTime = selectedDate;
      currentTime = currentTime.hour(setTime.get('hour'));
      currentTime = currentTime.minute(setTime.get('minutes'));
      this.props.fePickCurrentTime({ time: currentTime });
    }
    if (!this.props.isPosDeposit && openBookingProcedure.addedServices && openBookingProcedure.addedServices.length > 0) {
      this.props.updateNewBookingDate(selectedDate);
    }
    this.props.setView(true, selectedDate, undefined, this.props.openingHours);
    this.props.fetchProceduresBySalon(this.props.salonId, selectedDate);
    this.props.fetchEventsBySalon(this.props.salonId, selectedDate);
    const dateFormat = selectedDate.format('YYYYMMDD');

    if (this.props.show_only_active) {
      this.props.hideInactiveStylists(this.props.salonId, selectedDate);
    }

    if (setHistory) {
      const pathArray = this.props.history.location.pathname.split('/');
      if (pathArray.includes('week')) {
        pathArray.splice(3, 3, 'day', dateFormat);
      } else {
        pathArray.splice(3, 2, 'day', dateFormat);
      }
      this.props.history.push(`${pathArray.join('/')}`);
    }
  };

  setWeekView = (selectedDate, stylistId) => {
    const openBookingProcedure = this.props.booking;
    const urlWeekPrefix = `/salon/${this.props.salonId}/week`;

    if (openBookingProcedure.startTime) {
      const setTime = openBookingProcedure.startTime;
      let currentTime = selectedDate;
      currentTime = currentTime.hour(setTime.get('hour'));
      currentTime = currentTime.minute(setTime.get('minutes'));
      this.props.fePickCurrentTime({ time: currentTime });
    }

    if (
      !this.props.isPosDeposit &&
      openBookingProcedure.addedServices &&
      openBookingProcedure.addedServices.length > 0 /* &&
     this.props.displayStylists.length === 1 */
    ) {
      // commented the line below because it was causing the stylist to be selected on the first day of the week
      // GS-9870
      // this.props.updateNewBookingDate(selectedDate);
    }

    const bgnMoment = getSchedulerWeekStartMonday(selectedDate);
    const endMoment = getSchedulerWeekEndSunday(selectedDate);

    const dateFormat = formatSchedulerWeekUrlSegment(bgnMoment);

    let cStylistId = stylistId;

    if (stylistId === undefined || typeof stylistId === 'object') {
      cStylistId = this.props.stylists.filter((stylist) => stylist.status === 'active')[0].id;
    }

    if (USE_LOGGER_SCHEDAY) {
      const seg = formatSchedulerWeekUrlSegment(bgnMoment);
      const zBgn = fromAnyDateToZulu(bgnMoment);
      const zSel = fromAnyDateToZulu(selectedDate);
      const zEnd = fromAnyDateToZulu(endMoment);
      L_DAY.debug('Scheduler.setWeekView()', `${seg} x ${zBgn} x ${zSel} x ${zEnd}`);
    }

    this.props.setView(false, bgnMoment, cStylistId, this.props.openingHours);
    cStylistId = cStylistId ? String(cStylistId) : '';
    this.props.fetchProceduresBySalon(this.props.salonId, bgnMoment, endMoment, String(cStylistId));
    this.props.fetchEventsBySalon(this.props.salonId, bgnMoment, endMoment, String(cStylistId));

    if (!this.props.isPosDeposit && openBookingProcedure.addedServices && openBookingProcedure.addedServices.length > 0) {
      // this.props.updateNewBookingDate(selectedDate);
      if (this.props.displayStylists.length > 1) {
        this.props.history.push(`${urlWeekPrefix}/${dateFormat}/${cStylistId}/appointment/edit/${openBookingProcedure?.addedServices?.[0]?.id}/summary`);
      }
    } else {
      let sellVoucher = false;
      if (this.props.location.state && this.props.location.state.sellVoucher) {
        sellVoucher = true;
      }
      let pathname = `${urlWeekPrefix}/${dateFormat}/${cStylistId}`;
      if (window.location.href.indexOf('appointment/new/services') > 0) {
        pathname = `${urlWeekPrefix}/${dateFormat}/${cStylistId}/appointment/new/services`;
      }
      this.props.history.push({
        pathname,
        state: { sellVoucher },
      });
    }
  };

  onCancel = () => {
    this.setState({ modalIsOpen: false, depositAction: '' });
    this.props.clearNoshowChargeInfo();
    this.props.emptyAppointmentStatuses();
  };

  // FIXME: seems unused, delete it
  sendRebookSms = () => {
    this.props.clearNoshowChargeInfo();
    this.props.emptyAppointmentStatuses();
    this.setState({ depositAction: '' });
    const sortFunc = (a, b) => moment(a.start_time) - moment(b.start_time);

    if (this.props.appointmentDetails) {
      const details = Object.values(this.props.appointmentDetails);

      if (details.length && details[0].response) {
        const sortedProcudures = details[0].response.procedures.sort(sortFunc);
        if (details.length && details[0].response && details[0].response.client.stopped_sms) {
          this.setState({ smsStoppedModal: true, modalIsOpen: false });
        } else {
          this.setState({ procedure: sortedProcudures[0], modalIsOpen: false }, () => {
            this.setState({ showSms: true });
          });
        }
      }
    }
  };

  showRebook = (showRebookComponent) => void this.setState({ showRebookComponent });

  rebook = () => {
    const { emptyAppointmentStatuses, clearNoshowChargeInfo, appointmentDetails } = this.props;

    clearNoshowChargeInfo();
    emptyAppointmentStatuses();

    this.setState({ depositAction: '' });

    if (!appointmentDetails) return;

    const details = Object.values(appointmentDetails);
    if (!details?.length || !details?.[0]?.response) return;

    this.setState(
      {
        oldAppointment: { ...details[0].response },
        modalIsOpen: false,
      },
      () => void this.setState({ showRebookComponent: true }),
    );
  };

  onRequestOkClick = () => {
    this.setState({ modalRequestSend: false });
    this.props.clearRequestDepositSuccess();
  };

  closeNoSmsCreditModal = () => void this.setState({ modalNoSmsCredit: false });
  showSmsPopup = (showSms) => void this.setState({ showSms });

  gotoBilling = () => {
    const og = this.props.history.location.pathname;

    const pathname =
      og.indexOf('day') > 0
        ? `${og.split('day/')[0]}settings/billing`
        : og.indexOf('communications') > 0
          ? `${og.split('communications/')[0]}settings/billing`
          : `${og.split('week/')[0]}settings/billing`;

    this.props.history.push({ pathname });
  };

  handleVisibilityChange = (isVisible) => {
    if (isVisible) {
      // force a reload of events as things may have changed while we weren't visible
      switch (this.currentView()) {
        case 'day':
          {
            const currentDateString = this.props.match.params.dateString;
            if (currentDateString && currentDateString.toLowerCase() === 'today') {
              const today = moment();
              this.setDayView(today);
            } else if (currentDateString !== undefined) {
              const parseDate = moment(currentDateString, 'YYYYMMDD');
              if (parseDate.isValid()) {
                this.setDayView(parseDate, false);
              }
            }
          }
          break;
        case 'week':
          {
            const currentDateString = this.props.match.params.dateString;
            if (currentDateString !== undefined) {
              const parseWeek = parseSchedulerWeekUrlSegment(currentDateString, this.props.timezone);
              if (parseWeek && parseWeek.isValid()) {
                const currentStylistId = this.props.match.params.stylistId;
                if (currentStylistId !== undefined && Number(currentStylistId)) {
                  this.setWeekView(parseWeek, Number(currentStylistId));
                }
              }
            }
          }
          break;
        default:
          break;
      }
    }
  };

  updateProcedureStatus = (procedures, status, action = '', amount = 0, depositData, isCancelling = false) => {
    const FTAG = 'updateProcedureStatus()';

    if (!procedures) {
      L.error(FTAG, 'Invalid procedures:', procedures);
      return;
    }

    const { salonId, deleteAllAppointments, changeAppointmentsStatus } = this.props;

    if (status === 'deleteAll') {
      L.unignore(FTAG, 'deleteAllAppointments:', deleteAllAppointments(salonId, procedures));
      return;
    }

    let ds = '';
    if (depositData) {
      const depositAmount = depositData?.amount;
      const depositSource = depositData?.source;

      this.setState({ deposit_amount: depositAmount, deposit_source: depositSource, depositAction: action });
      ds = depositData.source;
    }

    if (isCancelling && status === AP_STATUS.noShow) status = AP_STATUS.canceled;

    const id = procedures?.[0]?.appointment?.id;
    const isRepeating = procedures?.[0]?.appointment?.is_repeating;
    const appointment = { id, status, action, amount, deposit_source: ds, is_repeating: isRepeating };

    L.unignore(FTAG, 'changeAppointmentsStatus:', changeAppointmentsStatus(salonId, [appointment]));
  };

  subscribe = () => {
    const channelName = `${this.props.salonId}_channel`;
    PUSHER.redux.subscribe({ channelName, eventName: 'appointment', actionType: SCHEDULER_UPDATED_APPOINTMENT });
    PUSHER.redux.subscribe({ channelName, eventName: 'procedure', actionType: SCHEDULER_UPDATED_PROCEDURE });
    PUSHER.redux.subscribe({ channelName, eventName: 'timeoff', actionType: SCHEDULER_UPDATED_EVENT });
    PUSHER.redux.subscribe({ channelName, eventName: 'sms_credits_available', actionType: SMS_CREDITS_PUSHER });
  };

  unsubscribe = () => {
    const channelName = `${this.props.salonId}_channel`;
    PUSHER.redux.unsubscribe({ channelName, eventName: 'appointment', actionType: SCHEDULER_UPDATED_APPOINTMENT });
    PUSHER.redux.unsubscribe({ channelName, eventName: 'procedure', actionType: SCHEDULER_UPDATED_PROCEDURE });
    PUSHER.redux.unsubscribe({ channelName, eventName: 'timeoff', actionType: SCHEDULER_UPDATED_EVENT });
    PUSHER.redux.unsubscribe({ channelName, eventName: 'sms_credits_available', actionType: SMS_CREDITS_PUSHER });
  };

  currentView = () => {
    let currentView = this.props.match.params.view;
    if (!currentView) {
      const pathArray = this.props.location.pathname.split('/');
      currentView = pathArray[3]; // eslint-disable-line prefer-destructuring
    }
    return currentView;
  };

  WeekView = () => void this.setDayView(this.props.currentDay, true);

  dayView = (id) => void this.setWeekView(this.props.selectedDate, id);

  changeDate = (date) => void this.setWeekView(date, this.props.stylistId);

  dayPick = (cDate) => {
    if (this.props.dayView) {
      this.props.setCurrentDay(cDate);
      this.setDayView(cDate, true);
    } else {
      this.setWeekView(cDate, this.props.stylistId);
    }
  };

  stylistSelect = (stylist) => void this.dayView(stylist.id);

  reloadEvents = () => {
    const startDate = getSchedulerWeekStartMonday(this.props.selectedDate);
    const endDate = getSchedulerWeekEndSunday(this.props.selectedDate);
    if (!this.props.dayView) {
      this.props.fetchEventsBySalon(this.props.salonId, startDate, endDate, String(this.props.stylistId));
    } else {
      this.props.fetchEventsBySalon(this.props.salonId, startDate /* , endDate */);
    }
  };

  notToRequestDeposit = () => {
    this.setState({ showNewAppointmentModal: false, newAppointment: [] });
    this.updateNewAppointmentList();
  };

  openSlideOutDepositPanel = () => {
    if (!isFullArray(this.state.newAppointment)) return;

    const appointmentId = this.state.newAppointment?.[0]?.id;
    this.setState({ newAppointment: [] });

    this.props.feSidebarOpen({ panel: SP.requestDeposit, appointmentId });
  };

  showRequestDeposit = () => {
    if (!this.props.sms_credits_available) {
      this.setState({ modalNoSmsCredit: true });
      return;
    }
    this.setState({ showNewAppointmentModal: false });
    const { newAppointment } = this.state;
    if (newAppointment.length > 0) {
      const appId = newAppointment.length > 0 ? newAppointment[0].id : '';
      this.setState({ newAppointment: [] });
      const elString = `appointment_${appId}`;
      const interval = setInterval(() => {
        const el = document.getElementById(elString);
        if (el) {
          el.click();
          clearInterval(interval);
          const interval2 = setInterval(() => {
            const requestEl = document.getElementById('request-deposit-btn');
            if (requestEl) {
              requestEl.click();
              clearInterval(interval2);
              const interval3 = setInterval(() => {
                const depositEl = document.getElementsByClassName('form-request')[0];
                let inputEl;
                if (depositEl) {
                  inputEl = depositEl.children[1].children[1];
                }
                if (inputEl) {
                  inputEl.value = Math.floor(this.state.totalPrice / 2);
                  // enable send button
                  document.querySelector(`[data-find-me-as="${ELID.findMeAsFormFooter}"]`).children[0].disabled = false;
                  clearInterval(interval3);
                  this.setState({ showRequestDepositPopup: true });
                }
              }, 500);
            }
          }, 500);
        }
      }, 500);
      // if after 3 seconds, we can't find the appointment, give up trying
      setTimeout(() => {
        clearInterval(interval);
        clearInterval(interval);
        clearInterval(interval);
      }, 3000);
    }
  };

  gotoCheckout = () => {
    const isWeek = fromPathnameToIsWeek(this.props.location?.pathname);

    if (this.props.oldAppointment) {
      navigateToCheckout({
        appointment: this.props.oldAppointment,
        history: this.props.history,
        isWeek,
        isDay: !isWeek,
        onClean: this.props.cleanCheckoutData,
        onData: this.props.setCheckoutInitData,
        pdi: this.props.procedures_date_index,
        procedures: this.props.oldAppointment.procedures.slice(),
        salonId: this.props.salonId,
        stylistId: this.props.stylistId,
        tag: 'Scheduler.gotoCheckout()',
      });
    }

    this.setState({ showGoToCheckoutModal: false, seenGoToCheckout: true });
    this.props.clearOldAppointment();
  };

  gotoToday = () => {
    this.props.clearOldAppointment();
    // clear the newRebookCreated so showGoToCheckoutModal won't be set again
    this.props.cleanCheckoutData();
    this.setState({ showGoToCheckoutModal: false, seenGoToCheckout: true, newAppointment: [] });

    const isDayView = this.props.dayView;

    if (isDayView) {
      this.setDayView(moment(), true);
    } else {
      const currentStylistId = this.props.stylistId || this.props.displayStylists?.[0]?.id;
      if (currentStylistId) {
        this.setWeekView(moment(), fromAnyToNumber(currentStylistId));
      }
    }
  };

  updateNewAppointmentList = () => {
    if (this.props.newAppointment.length > 0) this.props.removeNewAppointment();
  };

  closeAlert = () => void this.setState({ areAllStylistsOff: false });

  handleModalUploader = (stylistId) => {
    this.setState({
      modalUploader: !this.state.modalUploader,
      selectedStylistId: stylistId,
    });
    this.props.feSidebarClose({ tag: 'Scheduler.handleModalUploader()' });
  };

  getStylistById = (stylistId) => {
    const { stylists } = this.props;
    return stylists?.find((s) => s.id === stylistId) || null;
  };

  render() {
    const { depositAction, seenGoToCheckout, smsStoppedModal, modalUploader } = this.state;
    const { showGoToCheckoutModal, showNewAppointmentModal, showRequestDepositPopup } = this.state;
    const { isSidebarOpen, isPosDeposit, panel, t, stripeStatus } = this.props;

    const dateStringFromProps = this.props.match.params.dateString;

    const selectedStylist = this.getStylistById(this.state.selectedStylistId);
    const areAllStylistsOff = fromAnyToBool(this.state.areAllStylistsOff);

    const renderSmsDepositBanner = stripeStatus === STRIPE_STATUS.complete || stripeStatus === STRIPE_STATUS.restricted;

    const handleClose = () => this.setState({ modalIsOpen: false, smsStoppedModal: false });

    const isDayView = isTruthy(this.props.dayView);
    const isWeekView = isFalsy(this.props.dayView);

    const oldStatus = this.props.oldAppointment?.status;
    const isOldStatusCancelOrNoShow = oldStatus === AP_STATUS.canceled || oldStatus === AP_STATUS.noShow;
    const isRebookAppointmentModalOpen = !isPosDeposit && showGoToCheckoutModal && !seenGoToCheckout;

    if (isRebookAppointmentModalOpen) {
      L.debug('render()', 'isRebookAppointmentModalOpen', { isPosDeposit, showGoToCheckoutModal, seenGoToCheckout });
    }

    const gotoNewDeposit = this.openSlideOutDepositPanel.bind(this);
    const currentClient = this.props.clientList.find((c) => c.id === this.state.clientId);

    return (
      <PageVisibility onChange={this.handleVisibilityChange}>
        <div
          data-bem="Scheduler"
          className={cn({
            scheduler: true,
            [CN.shiftedLeft]: isSidebarOpen && panel !== SP.stylistLog && panel !== SP.staffList,
          })}
        >
          <StripeCapitalAutoLaunch />

          {modalUploader && (
            <ProfileImageUploader
              stylistId={selectedStylist?.id}
              initialImage={selectedStylist?.profile_image}
              stylistName={selectedStylist?.user_name}
              onClose={this.handleModalUploader}
              onSave={(ev) => {
                L.debug('render() > onSave()', 'ev:', ev);
                this.setState({ modalUploader: false });
              }}
            />
          )}
          <Suspense fallback={null}>
            <Sidebar />
          </Suspense>

          <div style={STYLE_DISPLAY_NONE}>
            {this.state.todayProcedures && this.state.todayProcedures.length && (
              <ComponentToPrint ref={this.componentRef} procedures={this.state.todayProcedures} stylistList={this.props.currentSalon.stylistList} dateString={dateStringFromProps} />
            )}
          </div>

          {this.props.booking.appointmentStatuses.isFetching && (depositAction !== DEPOSIT_ACTION.bankCharges || isNotNilValue(this.props.booking.appointmentStatuses.response)) && (
            <DepositLoadingPanel tag="Scheduler__image-loading" depositAction={depositAction} />
          )}

          <SchedulerHeaderWrap
            {...this.props}
            dayPick={this.dayPick}
            selectedView={isDayView ? 'Day' : 'Week'}
            stylists={this.props.personalDisplayStylists || this.props.stylists.filter((stylist) => stylist.status === 'active')}
            onShowWeekView={this.WeekView}
            onShowDayView={this.dayView}
            onSelectStylist={this.stylistSelect}
            dayView={isDayView}
            onChangeDate={this.changeDate}
            selectedDate={this.props.selectedDate}
            checked={false}
            id={this.props.salonId}
            onCheckboxClick={alwaysNoop}
            setWeekView={this.setWeekView}
            defaultToWeekForOneStylist={this.props.defaultToWeekForOneStylist}
            simulatClick={this.props.location.state && this.props.location.state.sellVoucher}
          >
            {this.state.todayProcedures && this.state.todayProcedures.length && (
              <ReactToPrint
                trigger={() => (
                  <Button tag="Scheduler__button-print" variant="chameleon" noMargin onClick={alwaysNoop} width="small" height="medium">
                    <Icon variant="printer" width="lg" selected />
                  </Button>
                )}
                content={() => this.componentRef.current}
              />
            )}
          </SchedulerHeaderWrap>
          <SmsPreferencesPopup smsConfirmSetting={this.props.smsConfirmSetting} salonId={this.props.salonId.toString()} currentSalon={this.props.currentSalon} history={this.props.history} />

          {this.props.calendarNoAccess && <CalendarNoAccess />}

          {!this.props.calendarNoAccess && isDayView && (
            <>
              {areAllStylistsOff && <AlertAllStylistsOff onClose={this.closeAlert} />}

              <ModalContext.Consumer>
                {({ showModal }) => (
                  <DayScheduler
                    {...this.props}
                    dayPick={this.dayPick}
                    procedureDropTime={this.props.procedureDropTime}
                    procedureDropTimeUndone={this.props.procedureDropTimeUndone}
                    setDropTime={this.props.setDropTime}
                    setWeekView={this.setWeekView}
                    showModal={showModal}
                    updateProcedureStatus={this.updateProcedureStatus}
                    messageSent={this.props.messageSent}
                    clientList={this.props.clientList}
                    hideDeletedStylist={this.props.isHideDeletedStylistColumn}
                    procedureDragFrom={this.props.procedureDragFrom}
                    setDragFrom={this.props.setDragFrom}
                    currentDay={this.props.currentDay}
                    hide_non_working_stylist_column={this.props.hide_non_working_stylist_column}
                    handleProfileImageClick={this.handleModalUploader}
                  />
                )}
              </ModalContext.Consumer>
            </>
          )}

          {/* // week view */}
          {!this.props.calendarNoAccess && isWeekView && (
            <ModalContext.Consumer>
              {({ showModal }) => (
                <WeekScheduler
                  {...this.props}
                  client={currentClient}
                  showModal={showModal}
                  onChangeDate={this.changeDate}
                  updateProcedureStatus={this.updateProcedureStatus}
                  selectedDate={this.props.selectedDate}
                  isOneStylist={this.props.displayStylists.length === 1}
                  authenticationRequired={this.state.authenticationRequired}
                  messageSent={this.props.messageSent}
                  clientList={this.props.clientList}
                  procedureDragFrom={this.props.procedureDragFrom}
                  displayStylists={this.props.displayStylists}
                  addedServices={!isPosDeposit ? this.props.addedServices : []}
                  isPosDeposit={isPosDeposit}
                />
              )}
            </ModalContext.Consumer>
          )}

          <ToggleFeature flag="showRequestDeposit">
            {renderSmsDepositBanner && showNewAppointmentModal && !showGoToCheckoutModal && !showRequestDepositPopup && (
              <div className="new-appointment-notification">
                <div className="notify-text">
                  {t('scheduler.requestDepositFrom')} {this.state.clientName} {t('scheduler.viaSlickPay')}
                </div>

                <div data-bem="Scheduler__godep--enabled" className="yes-btn" role="button" tabIndex={-1} onClick={gotoNewDeposit}>
                  {t('common.buttons.yes')}
                </div>

                <div className="no-btn" role="button" tabIndex={-1} onClick={this.notToRequestDeposit}>
                  {t('common.buttons.no')}
                </div>
              </div>
            )}
          </ToggleFeature>

          <RebookAppointmentModal
            tag="Scheduler__modal-rebook"
            isOpen={isRebookAppointmentModalOpen}
            onCheckout={this.gotoCheckout}
            onView={this.stayOnCurrentDay}
            onToday={this.gotoToday}
            hideCheckout={isOldStatusCancelOrNoShow}
          />

          {this.state.showRebookComponent && this.props.location.pathname.indexOf('appointment/new/services') < 0 && (
            <RebookAppointment
              oldAppointment={this.state.oldAppointment}
              from="cancel"
              showRebook={this.showRebook}
              fetchProceduresBySalon={this.props.fetchProceduresBySalon}
              fetchEventsBySalon={this.props.fetchEventsBySalon}
            />
          )}

          {this.state.showSms && <SmsButtonNew procedure={this.state.procedure} bucketType="appointment" smsComposeModalOpen={this.state.showSms} showParentSmsPopup={this.showSmsPopup} cancelled />}

          <SalonModal tag="Scheduler__modal-deposit-request-sent" isOpen={this.state.modalRequestSend} variant="noFooter">
            <SalonModal.Header>
              <div className={SM.closeButtonContainer}>
                <Button variant="empty" onClick={this.onRequestOkClick}>
                  <Icon variant="close" variantInvert />
                </Button>
              </div>
            </SalonModal.Header>
            <SalonModal.Body>
              <IconStatus width="lg" variant="success">
                <Icon selected width="l1" variant="confirm" />
              </IconStatus>

              <div className={SM.modalContent}>
                <div className={SM.modalTitle}>{t('scheduler.depositRequestModal.title')}</div>
                <div className={SM.modalText}>{t('scheduler.depositRequestModal.description')}</div>
              </div>
            </SalonModal.Body>
          </SalonModal>

          {this.state.modalNoSmsCredit && (
            <NoSmsCredit
              history={this.props.history}
              isModalAction={false}
              closeNoSmsCreditModal={this.closeNoSmsCreditModal}
              purchaseSmsBundle={this.props.purchaseSmsBundle}
              salonId={this.props.salonId}
              errorMessage={this.props.purchaseError}
              gotoBilling={this.gotoBilling}
              isFetching={this.props.isFetching}
              sms_credits_available={this.props.sms_credits_available}
              permission={this.props.permission}
            />
          )}

          {smsStoppedModal && (
            <AlertDialog title="" buttonText="Close" onClick={handleClose}>
              <SmsStoppedTextComponent />
            </AlertDialog>
          )}
        </div>
      </PageVisibility>
    );
  }
}

// adapter for the arguments
const smsConfirmSetting = (sendConfirmationSms) => bePutSalonFlagSettings({ send_confirmation_sms: sendConfirmationSms });

const SchedulerHoc = withRouter(
  connect(
    (state, ownProps) => {
      const personalStylists = ownProps?.personalDisplayStylists;
      const accessStylistId = ownProps?.personalStylistId;
      const fullStylists = state.salon.stylistList.response;

      const schedulerState = state.scheduler;

      return {
        panel: selectSidebarPanel(state),
        isSidebarOpen: selectSidebarOpen(state),
        openingHours: selectCurrentSalonOpeningHours(state),
        hideNoShow: state.currentSalon.noshow_behaviour === 'hide_in_view',
        timezone: state.currentSalon.timezone,
        salonId: state.currentSalon.id,
        booking: selectBooking(state),
        categoriesAndServices: state.currentSalon.services,
        displayNotification,
        closeNotification,
        ...schedulerState,
        ...(personalStylists ? { displayStylists: personalStylists, stylistId: accessStylistId } : {}),
        noshowChargedAmount: schedulerState.noshowChargedAmount,
        appointmentDetails: schedulerState.appointmentDetails,
        noshow_appointment_id: schedulerState.noshow_appointment_id,
        noshowChargeSuccess: schedulerState.noshowChargeSuccess,
        noshowChargeError: schedulerState.noshowChargeError,
        depositData: schedulerState.depositData,
        errorMessage: schedulerState.errorMessage,
        noshow_action: schedulerState.noshow_action,
        isPosDeposit: schedulerState.isPosDeposit,
        isHideDeletedStylistColumn: selectCurrentSalonHideDeletedStylistColumn(state),
        procedureDragFrom: schedulerState.procedureDragFrom,
        selectedDate: schedulerState.selectedDate,
        stylistId: personalStylists ? accessStylistId : schedulerState?.stylistId,
        appointmentCancelled: schedulerState.appointmentCancelled,
        lastChangedAppointmentStatus: schedulerState.lastChangedAppointmentStatus,
        isPending: schedulerState.isFetching,
        newAppointment: selectBookingNewAppointment(state),
        oldAppointment: selectBookingOldAppointment(state),
        newRebookCreated: selectBookingNewRebookCreated(state),
        addedServices: state.booking.addedServices,
        messageSent: state.clientCommunications.messageSent,
        purchaseError: state.clientCommunications.errorMessage,
        sms_credits_available: state.clientCommunications.sms_credits_available,
        isFetching: state.clientCommunications.isFetching,
        currentSalon: state.currentSalon,
        clientList: state.currentSalon.clientList,
        permission: selectEffectiveUserRole(state),
        hide_non_working_stylist_column: state.currentSalon.hide_non_working_stylist_column,
        stylists: personalStylists || fullStylists,
        salonFlags: selectCurrentSalonFlags(state),
        stripeStatus: selectCurrentSalonStripeStatus(state),
      };
    },
    mapActionsToDispatchToProps({
      // actions from src/scheduler/actions.js
      setCreation,
      setModal,
      pickCurrentStylist,
      createIfAllowed,
      updateNewBookingDate,
      fetchProceduresBySalon,
      fetchEventsBySalon,
      deleteAllAppointments: deleteAllAppointmentsV1,
      updateProcedure,
      reorderStylists,
      saveStylistOrder,
      reorderStylistsThenSave,
      hideInactiveStylists,
      showAllStylist,
      setView,
      setCurrentDay,
      fetchAppointmentDetails,
      fetchClientLabels,
      fetchDeposit,
      savePreference,
      getProcedureLog,
      revertProcedureLog,
      deletePreference,
      markAppointmentEdit,
      setBookedByButton,
      setDropTime,
      undoProcedure,
      requestDeposit,
      fePickCurrentTime,
      fetchSalon,
      fetchStylistsBySalon,
      createUpdateTimeoff,
      deleteAppointment,
      updateService,
      displayNotification,
      closeNotification,
      changeAppointmentsStatus,
      trackEvent,
      getCategories,
      feSetBookingStartTime,
      setEndTime,

      emptyAppointmentStatuses,
      clearNoshowChargeInfo,
      clearDeleteAppointmentInfo,
      removeNewAppointment,

      setDragFrom,
      setCheckoutInitData: setupCheckout,
      clearOldAppointment: feClearOldAppointment,
      cleanCheckoutData: feCleanCheckoutData,
      clearRequestDepositSuccess,
      purchaseSmsBundle,
      feSidebarOpen,
      feSidebarClose,
      smsConfirmSetting,
    }),
  )(withTranslation()(Scheduler)),
);

const CalendarNoAccess = () => {
  const { t } = useTranslation();

  return (
    <div data-bem="CalendarNoAccess" className={CN.noAccess}>
      <div className={CN.noAccessInner}>
        <Icon variant="lock" width="l1" />
        <h2>{t('scheduler.calendarNoAccess.title')}</h2>
        <p>{t('scheduler.calendarNoAccess.description')}</p>
      </div>
    </div>
  );
};

const SchedulerWithCalendarPermissions = (props) => {
  const { isPersonalOnly, accessStylistId, hasLockToken, canViewAll, canViewPersonal } = useIsPersonalCalendarOnly();
  const fullStylists = useSelector((s) => s.salon.stylistList.response);

  const personalDisplayStylists = useMemo(() => (isPersonalOnly ? fullStylists.filter((s) => s.id === accessStylistId) : null), [isPersonalOnly, accessStylistId, fullStylists]);

  const personalStylistHasColumn = personalDisplayStylists?.[0]?.has_column === true;

  const appointmentPerms = useAppointmentPermissions();
  const timeOffPerms = useTimeOffPermissions();

  if (!hasLockToken) {
    return <SchedulerHoc {...props} />;
  }

  if (!canViewAll && !canViewPersonal) {
    return <SchedulerHoc {...props} calendarNoAccess />;
  }

  if (isFullArray(personalDisplayStylists)) {
    return (
      <SchedulerHoc
        {...props}
        personalDisplayStylists={personalDisplayStylists}
        personalStylistId={accessStylistId}
        calendarPermissionPersonal
        calendarNoAccess={!personalStylistHasColumn}
        appointmentPerms={appointmentPerms}
        timeOffPerms={timeOffPerms}
      />
    );
  }

  return <SchedulerHoc {...props} appointmentPerms={appointmentPerms} timeOffPerms={timeOffPerms} />;
};

export default SchedulerWithCalendarPermissions;
